import { LiveMapNotices } from './map/LiveMapNotices';
import { MapCanvasControls } from './map/MapCanvasControls';
import { BRIDGE_DASH, LINE_CORE_OPACITY, LINE_GLOW_OPACITY, LINE_GLOW_WIDTH_SCALE, headFeatures, landingFeatures, lineFeature, meshcorePayloadTypeName, prefersReducedMotion, walkState, type FxFeature } from '../lib/pathFx';
import { packetCategoryInfo } from '../lib/definitions/protocols';
import { MapLegends } from './MapLegends';
import { useAppData } from '../state/AppData';
import { useRef, useState, useEffect } from 'react';
import { Map as MLMap, NavigationControl, type MapLibreEvent, type MapLayerMouseEvent, type GeoJSONSource } from 'maplibre-gl';
import { buildOsmStyle, RASTER_PAINT, MAP_CONTAINER_BACKGROUND } from '../lib/mapStyle';
import { WORLD_VIEW_CENTER, WORLD_VIEW_ZOOM, MESHVIEW_FEEDER_LINE_LABEL, CORESCORE_PATH_LINE_LABEL, MESH_FIT_PADDING_PX, MESH_FIT_MAX_ZOOM, meshviewRouteConfidenceStyle, meshviewRouteProvenanceStyle, meshviewRouteLineColor, meshviewFlashStyle, MESHVIEW_FLASH_GROW_MS, MESHVIEW_FLASH_START_RADIUS_SCALE, MESHVIEW_FLASH_FADE_SHRINK_FLOOR, MESHVIEW_FLASH_COLOR, CORESCORE_PATH_LINE_STYLE, CORESCORE_PATH_FLASH_STYLE, MESH_FIT_CONTROL_LABEL } from '../lib/definitions/map';
import { IN_FLIGHT_COLOR, REGION_BOUNDARY_COLOR, PROTOCOL_COLOR, UNKNOWN_PROTOCOL_LABEL } from '../lib/definitions/protocols';
import { colorFor } from '../lib/definitions/palette';
import { TRUST_TIER_INFO, PROVENANCE_SCOPE_LABEL } from '../lib/definitions/trust';
import { routeWalkPathState, MESHVIEW_TRAIL_HEAD_OPACITY } from '../lib/animation';
import { trailSlices, positionAlongPath } from '../lib/meshview';
import { corescoreTrailSlices, corescorePositionAlongPath } from '../lib/corescore';
import { NO_CONNECTIONS_EMPTY_STATE } from '../lib/definitions/navigation';
import { navigate } from '../lib/router';
import { nodesToGeoJSON, nodeColorExpr, SCOPE_OPACITY_EXPR, SCOPE_MODE_OPACITY_EXPR, simulatedNodesToGeoJSON, protocolColorExpr, meshBoundsFromNodes } from '../lib/liveMapData';
import { useRegionReach } from "./map/useRegionReach";
import { useMeshMapperCoverage } from "./map/useMeshMapperCoverage";
import { useFreqMapperCoverage } from "./map/useFreqMapperCoverage";
import { layerCells, mountHexLayer, removeHexLayer, type HexLayerCells } from "./map/hexCoverageLayer";
import { cellsFromHexRows } from "../lib/hexCoverage";
import { coverageDrawn, fillOpacity, typeExpression } from "../lib/meshmapperCoverage";
import { cellsFromRows, fmColourExpression, fmDrawn } from "../lib/freqmapperCoverage";
import { hexToRgba, reachGeoJSON, reachHeatColour, REACH_HEAT_LAYER, REACH_HEAT_SOURCE, reachKey } from "../lib/regionReach";
import { useMapScopes } from './map/useMapScopes';
import type { RegionScopeArgs } from '../lib/liveMapData';
import { useAnimationOverlay } from './map/useAnimationOverlay';
import "maplibre-gl/dist/maplibre-gl.css";


// The layers one walk source draws, bottom to top: a wide soft glow under the line, the dashed bridge for unresolved
// stretches, the line, then the landing effects (orb, ripple), the head (glow and dot) and the small solid burst.
// Features carry a `kind` (lib/pathFx.ts) that picks the layer. `id` is both the source and the line layer's id.
function walkLayerOrder(id: string, headsId: string): string[] {
  return [`${id}-glow`, `${id}-bridge`, id, `${id}-orb`, `${id}-ripple`, `${id}-headglow`, headsId, `${id}-burst`];
}

function addWalkLayers(map: MLMap, id: string, headsId: string) {
  const width = (k: number) => ["interpolate", ["linear"], ["zoom"], 3, ["*", 1 * k, ["get", "widthScale"]], 8, ["*", 1.6 * k, ["get", "widthScale"]], 14, ["*", 2.6 * k, ["get", "widthScale"]]] as any;
  const kind = (k: string) => ["==", ["get", "kind"], k] as any;
  map.addLayer({ id: `${id}-glow`, type: "line", source: id, filter: kind("seg"), layout: { "line-cap": "round" }, paint: { "line-color": ["get", "color"], "line-width": width(LINE_GLOW_WIDTH_SCALE), "line-opacity": ["*", ["get", "opacity"], LINE_GLOW_OPACITY], "line-blur": 3 } });
  map.addLayer({ id: `${id}-bridge`, type: "line", source: id, filter: kind("bridge"), paint: { "line-color": ["get", "color"], "line-width": width(1), "line-opacity": ["get", "opacity"], "line-dasharray": BRIDGE_DASH } });
  map.addLayer({ id, type: "line", source: id, filter: kind("seg"), layout: { "line-cap": "round" }, paint: { "line-color": ["get", "color"], "line-width": width(1), "line-opacity": ["get", "opacity"] } });
  map.addLayer({ id: `${id}-orb`, type: "circle", source: id, filter: kind("orb"), paint: { "circle-color": ["get", "color"], "circle-radius": ["get", "radiusPx"], "circle-opacity": ["get", "opacity"], "circle-blur": 0.5 } });
  map.addLayer({ id: `${id}-ripple`, type: "circle", source: id, filter: kind("ripple"), paint: { "circle-color": "rgba(0,0,0,0)", "circle-radius": ["get", "radiusPx"], "circle-stroke-width": 1.2, "circle-stroke-color": ["get", "color"], "circle-stroke-opacity": ["get", "opacity"] } });
  map.addLayer({ id: `${id}-headglow`, type: "circle", source: id, filter: kind("headglow"), paint: { "circle-color": ["get", "color"], "circle-radius": ["get", "radiusPx"], "circle-opacity": ["get", "opacity"], "circle-blur": 1 } });
  map.addLayer({ id: headsId, type: "circle", source: id, filter: kind("head"), paint: { "circle-color": ["get", "color"], "circle-radius": ["get", "radiusPx"], "circle-opacity": ["get", "opacity"], "circle-stroke-width": 0.8, "circle-stroke-color": "#ffffff", "circle-stroke-opacity": 0.7 } });
  map.addLayer({ id: `${id}-burst`, type: "circle", source: id, filter: kind("burst"), paint: { "circle-color": ["get", "color"], "circle-radius": ["get", "radiusPx"], "circle-opacity": ["get", "opacity"], "circle-stroke-width": 0.8, "circle-stroke-color": "#ffffff", "circle-stroke-opacity": ["*", ["get", "opacity"], 0.7] } });
}

/** The packet-type colour of a packet, in the current theme. */
function packetTypeColour(protocol: string | null | undefined, rawType: string | null | undefined, theme: "light" | "dark"): string {
  return colorFor(packetCategoryInfo(protocol, rawType).color, theme);
}


export function LiveMap() {
  const {
    nodes,
    animations,
    effectiveTheme,
    select,
    selection,
    overflowCount,
    protocolFilter,
    tagFilter,
    provenanceScope,
    setProvenanceScope,
    meshviewRouteWalks,
    reportMeshviewFrameMs,
    corescorePathWalks,
    replay,
    exitReplay,
    selectedRegions,
    hideOutsideRegions,
    regionsForNode,
    nearRegionBoundary,
    simulatedNodes,
    displaySettings,
    displaySettingsAvailable,
    availableProtocols,
    healthEverLoaded,
  } = useAppData();
  const { colourBy, view: scopeView, colours: scopeColours } = useMapScopes();
  const { selected: reachSelected, scope: reachScope } = useRegionReach();
  const mm = useMeshMapperCoverage();
  const mmShown = coverageDrawn({ available: mm.available, layerOn: mm.layerOn, hasSquares: mm.full !== null && mm.full.hexes.length > 0, protocolFilter });
  // The hexes of the stored response as cells, with the outline edges worked out from them once per response.
  const mmCellsRef = useRef<{ version: string; layer: HexLayerCells | null } | null>(null);
  // FreqMapper coverage (issue #36): the same layer machinery, hexes from the answer the server built, drawn above MeshMapper.
  const fm = useFreqMapperCoverage();
  const fmShown = fmDrawn({ available: fm.available, layerOn: fm.layerOn, hasHexes: fm.full !== null && fm.full.hexes.length > 0, protocolFilter });
  const fmCellsRef = useRef<{ version: string; layer: HexLayerCells | null } | null>(null);
  const scopeViewRef = useRef(scopeView);
  scopeViewRef.current = scopeView;
  const byScope = colourBy === "scope" && scopeView !== null;
  const byScopeRef = useRef(byScope);
  byScopeRef.current = byScope;
  const regionScope: RegionScopeArgs = { selectedCount: selectedRegions.size, hideOutside: hideOutsideRegions, regionsForNode };
  const selectedNodeId = selection?.kind === "node" ? selection.id : null;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const [hover, setHover] = useState<{ x: number; y: number; label: string } | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapUnavailable, setMapUnavailable] = useState(false);
  const [unpositionedNodes, setUnpositionedNodes] = useState<{ id: string; name: string }[]>([]);
  const [unknownProtocolCount, setUnknownProtocolCount] = useState(0);
  const simulatedNodesRef = useRef(simulatedNodes);
  simulatedNodesRef.current = simulatedNodes;

  // The map's basemap always follows the app theme now — the floating
  // Auto/Light/Dark picker that used to sit over the map's top-left corner
  // was pure duplicate UI (the header's own ThemeToggle already controls
  // this exact setting) and it covered real map area. Removed 2026-09-20;
  // there is no independent basemap choice left to track.
  const resolvedBasemap: "light" | "dark" = effectiveTheme;

  const selectRef = useRef(select);
  selectRef.current = select;
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const filterRef = useRef(protocolFilter);
  filterRef.current = protocolFilter;
  const regionScopeRef = useRef(regionScope);
  regionScopeRef.current = regionScope;
  const selectedNodeIdRef = useRef(selectedNodeId);
  selectedNodeIdRef.current = selectedNodeId;
  const tagFilterRef = useRef(tagFilter);
  tagFilterRef.current = tagFilter;
  const nearRegionBoundaryRef = useRef(nearRegionBoundary);
  nearRegionBoundaryRef.current = nearRegionBoundary;
  const meshviewRouteWalksRef = useRef(meshviewRouteWalks);
  meshviewRouteWalksRef.current = meshviewRouteWalks;
  const corescorePathWalksRef = useRef(corescorePathWalks);
  corescorePathWalksRef.current = corescorePathWalks;

  // --- initial camera fit (2026-09-21) --------------------------------
  // hasAutoFitRef: flips true the one time the initial fit-to-mesh runs, so
  // it never fires again for the life of this map instance (fit is a
  // first-load thing, not a standing behaviour that re-centres on every
  // poll). userInteractedRef: flips true the moment the MAP ITSELF reports a
  // user-caused camera move (see the "movestart" listener below, which
  // checks e.originalEvent — present only for a real mouse/touch/wheel
  // input, absent for our own programmatic fitBounds/jumpTo calls) — once
  // set, the auto-fit effect below refuses to run at all, which is the
  // actual fix for the failure mode this addresses: an auto-fit that fights
  // the user is worse than no auto-fit.
  const hasAutoFitRef = useRef(false);
  const userInteractedRef = useRef(false);

  const NODE_LAYER_IDS = ["nodes-dots"];

  // --- init map once ------------------------------------------------
  useEffect(() => {
    if (!containerRef.current) return;
    let map: MLMap;
    try {
      map = new MLMap({
      container: containerRef.current,
      style: buildOsmStyle(effectiveTheme),
      // Provisional camera: a wide world view, corrected below (the auto-fit
      // effect) the moment we know whether map.default_center_*/
      // default_zoom (GET /api/config/display) is configured, or a node
      // position is known — never a hardcoded regional default (see
      // WORLD_VIEW_CENTER's own comment, definitions.ts).
      center: WORLD_VIEW_CENTER,
      zoom: WORLD_VIEW_ZOOM,
      attributionControl: { compact: true },
      // Required for the canvas to show up in screenshots / toDataURL
      // (headless verification, and any future "export map image" feature)
      // — without it the GL default framebuffer can be cleared before a
      // screenshot compositor reads it. Nested under canvasContextAttributes
      // as of maplibre-gl v6 (moved from a top-level MapOptions field).
      canvasContextAttributes: { preserveDrawingBuffer: true },
    });
    } catch (error) {
      console.error("Map initialization failed", error);
      containerRef.current.replaceChildren();
      setMapUnavailable(true);
      return;
    }
    mapRef.current = map;
    map.addControl(new NavigationControl({ showCompass: false }), "top-right");

    // Only a real user-driven camera move should ever suppress the initial
    // auto-fit (see hasAutoFitRef/userInteractedRef's own comment above).
    // MapLibre sets `originalEvent` on this event for a genuine mouse/touch/
    // wheel-driven move and leaves it unset for a programmatic one (jumpTo/
    // easeTo/flyTo/fitBounds) — including this component's own fitBounds
    // calls below — so this listener never mistakes our own fit for user
    // interaction.
    map.on("movestart", (e: MapLibreEvent) => {
      if (e.originalEvent) userInteractedRef.current = true;
    });

    function addNodeLayers() {
      if (map.getSource("nodes")) return;
      const built = nodesToGeoJSON(nodesRef.current, filterRef.current, regionScopeRef.current, selectedNodeIdRef.current, nearRegionBoundaryRef.current, tagFilterRef.current, scopeViewRef.current);
      map.addSource("nodes", { type: "geojson", data: built.collection });
      setUnpositionedNodes(built.unpositionedNodes);
      setUnknownProtocolCount(built.unknownProtocol);

      // Soft density glow beneath clusters (spec §2) — a single-hue,
      // low-opacity heatmap approximating a blurred halo rather than a
      // multi-colour heatmap gradient. Out-of-scope nodes drop to zero
      // weight so the glow reflects the current protocol scope too. Kept
      // deliberately restrained (no halos or glows
      // competing for attention) — this is the one soft-edge effect that
      // survives, at low opacity, purely as a coverage cue under the dots,
      // never on top of them.
      map.addLayer({
        id: "nodes-heat",
        type: "heatmap",
        source: "nodes",
        maxzoom: 14,
        paint: {
          "heatmap-weight": ["case", ["get", "inScope"], 0.5, 0],
          "heatmap-intensity": 0.5,
          "heatmap-radius": 22,
          "heatmap-opacity": 0.25,
          "heatmap-color": [
            "interpolate",
            ["linear"],
            ["heatmap-density"],
            0,
            "rgba(0,0,0,0)",
            1,
            effectiveTheme === "dark" ? "rgba(120,170,255,0.9)" : "rgba(60,100,180,0.55)",
          ],
        },
      });

      // meshview reception fan-out (live map, 2026-09-21) — every single
      // packet hits many feeders, and it should be VERY busy on the map;
      // a decrypted packet gets its lines drawn. One GL source, two layers, NOT
      // a DOM element per line/head (see lib/definitions.ts's
      // MESHVIEW_MAX_CONCURRENT_RECEPTION_PACKETS comment for the full
      // performance reasoning) — the same FeatureCollection carries both
      // each line's LineString (transmitter -> current head position, which
      // itself grows every frame — see the render effect further down) and,
      // only while that line is still travelling, a Point at its own head.
      // A "line" layer only ever draws LineString features and a "circle"
      // layer only ever draws Point features from the same source, so no
      // extra per-feature discriminator property is needed. Widened from a
      // flat 1px after seeing the fan animate but read as secondary
      // to the inbound first-party ray: the outward fan is the dominant
      // motion in firehose scope and should read that way, to a zoom-scaled
      // width, still a hair under the node dots' own radius scale so it
      // never competes with them. Line colour reuses
      // TRUST_TIER_INFO.external_unauthenticated's own colour rather than
      // inventing one — these lines ARE that trust tier; the travelling
      // head instead reuses IN_FLIGHT_COLOR, the same "something is moving
      // right now" colour the first-party ray's own marker already uses —
      // reusing that visual language rather than inventing a second one.
      // Added BEFORE nodes-dots/label so real node markers and their
      // click/hover targets stay on top of a busy fan-out, never occluded
      // by it.
      map.addSource("meshview-reception-lines", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      // Heads/flashes (circle layer) added BEFORE the line layer, so the
      // line layer paints ON TOP of them (2026-09-21, line-visibility fix).
      // Verified live with temporary instrumentation (a `window` hook onto
      // the held walks and this source's own feature list, removed again
      // once verified — never committed): `buildRouteWalk` was already
      // returning healthy path counts per walk (median several, up to 19)
      // and those paths were already landing in this GeoJSON source as real
      // LineString features with sane coordinates/opacity/colour — the data
      // pipeline was never broken. What made them read as "no lines, only
      // pulses" was pixel-level: most justified hops are genuinely short
      // (median under 10km — see buildRouteWalk's own Task 31 comment),
      // often only a few px at a normal zoom, a non-traceroute line's colour
      // is the SAME external_unauthenticated swatch a flash uses (by design
      // — both are honestly the same trust tier), and a flash's own peak
      // radius (7.5-12px, deliberately bigger than a node dot) was drawn
      // AFTER (on top of) the line layer in the old order — so a short line
      // ending at/near a node that also flashes was routinely painted over
      // by that flash before the frame ever reached the screen, confirmed
      // by direct pixel sampling of captured frames (isolated
      // external_unauthenticated-coloured pixels in the map pane were
      // dominated by one ~16px flash blob; genuine line strokes surfaced
      // only as a handful of 2-13px fragments, and only in frames where no
      // flash happened to sit on top of them). This does not change any
      // colour, width, opacity, confidence gate, or timing — only which of
      // the two already-correct layers paints last.
      //
      // Update, 2026-09-21 (colour-not-size fix): z-order alone was not
      // enough — a line and a flash of the SAME colour still blend where a
      // short (median <10km, ~6px) line is drawn entirely inside a flash's
      // own 24px blob. An inferred-provenance line now gets its own colour,
      // MESHVIEW_ROUTE_LINE_INFERRED_COLOR, distinct from the flash's own
      // MESHVIEW_FLASH_COLOR — see both constants' comments in
      // lib/definitions.ts. z-order and sizing above are unchanged.
      addWalkLayers(map, "meshview-reception-lines", "meshview-reception-heads");
      map.on("mouseenter", "meshview-reception-lines", (e: MapLayerMouseEvent) => {
        map.getCanvas().style.cursor = "default";
        setHover({ x: e.point.x, y: e.point.y, label: MESHVIEW_FEEDER_LINE_LABEL });
      });
      map.on("mousemove", "meshview-reception-lines", (e: MapLayerMouseEvent) => {
        setHover((prev) => (prev ? { ...prev, x: e.point.x, y: e.point.y } : prev));
      });
      map.on("mouseleave", "meshview-reception-lines", () => {
        map.getCanvas().style.cursor = "";
        setHover(null);
      });

      // CoreScope carried-path walks (live map, 2026-09-21) — the MeshCore
      // sibling of the meshview reception fan-out above. A SEPARATE GL
      // source/pair of layers, not a repurposing of meshview's own —
      // CoreScope's data has no confidence/provenance axis to style, and
      // keeping the two sources independent means neither feature's cap or
      // fade timing can ever bleed into the other's feature list. Same
      // two-layer shape (a line layer for the segment itself, a circle
      // layer carrying both each travelling head and every hop's own
      // arrival flash) and the same z-order convention meshview's own
      // layers settled on above: heads/flashes added BEFORE the line layer
      // (so the line paints on top of them), and both added before
      // "nodes-dots" so real node click/hover targets stay reachable over a
      // busy path — moved back above the node layers afterward (see the
      // moveLayer call near the end of this function) for the same
      // short-line-under-a-flash-or-dot visibility reason documented on
      // meshview's own moveLayer call.
      map.addSource("corescore-path-lines", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      addWalkLayers(map, "corescore-path-lines", "corescore-path-heads");
      map.on("mouseenter", "corescore-path-lines", (e: MapLayerMouseEvent) => {
        map.getCanvas().style.cursor = "default";
        setHover({ x: e.point.x, y: e.point.y, label: CORESCORE_PATH_LINE_LABEL });
      });
      map.on("mousemove", "corescore-path-lines", (e: MapLayerMouseEvent) => {
        setHover((prev) => (prev ? { ...prev, x: e.point.x, y: e.point.y } : prev));
      });
      map.on("mouseleave", "corescore-path-lines", () => {
        map.getCanvas().style.cursor = "";
        setHover(null);
      });

      // Every node, one shape, one size scale, colour only (see this file's top-of-section comment and
      // lib/definitions.ts's colour-scheme block). No stroke, no ring, no per-protocol shape: small and tight is the
      // point. `nodeColorExpr` (swapped via setPaintProperty, see the mode-change effect below) is the ONLY thing
      // that changes between ALL mode and single-protocol mode.
      map.addLayer({
        id: "nodes-dots",
        type: "circle",
        source: "nodes",
        // Higher drawOrder is drawn on top: big Meshtastic routers underneath, real-scope repeaters on top.
        layout: { "circle-sort-key": ["get", "drawOrder"] },
        paint: {
          // One zoom curve for every dot, scaled per marker (1.5 for Meshtastic routers, 0.75 for Unknown-scope repeaters).
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 3, ["*", 2, ["get", "dotScale"]], 8, ["*", 3.2, ["get", "dotScale"]], 14, ["*", 5.2, ["get", "dotScale"]]],
          "circle-color": nodeColorExpr(effectiveTheme, byScopeRef.current),
          "circle-opacity": byScopeRef.current ? SCOPE_MODE_OPACITY_EXPR : SCOPE_OPACITY_EXPR,
        },
      });

      // Boundary-node marker (spec §10) — a hollow ring around any node the
      // region view flags as near a selected region's edge. Transparent
      // fill + stroke only, so it never competes with "nodes-dots"'s own
      // colour-means-protocol/role convention; deliberately NOT tied to
      // SCOPE_OPACITY_EXPR (stays at full opacity even if the node itself
      // is dimmed) since the point is to call this node out regardless of
      // which side of the filter it happens to be on.
      map.addLayer({
        id: "nodes-boundary-ring",
        type: "circle",
        source: "nodes",
        filter: ["==", ["get", "nearBoundary"], true],
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 3, 4, 8, 6, 14, 10],
          "circle-color": "rgba(0,0,0,0)",
          "circle-stroke-width": 1.5,
          "circle-stroke-color": colorFor(REGION_BOUNDARY_COLOR, effectiveTheme),
          "circle-stroke-opacity": 0.9,
        },
      });

      map.addLayer({
        id: "nodes-label",
        type: "symbol",
        source: "nodes",
        minzoom: 9,
        layout: {
          "text-field": ["get", "name"],
          "text-size": 11,
          "text-offset": [0, 1.1],
          "text-anchor": "top",
          "text-optional": true,
        },
        paint: {
          "text-color": effectiveTheme === "dark" ? "#ffffff" : "#0b0b0b",
          "text-halo-color": effectiveTheme === "dark" ? "#1a1a19" : "#fcfcfb",
          "text-halo-width": 1.2,
          "text-opacity": SCOPE_OPACITY_EXPR,
        },
      });

      // Second line-visibility fix (2026-09-21): "nodes-dots" above is
      // itself ABOVE "meshview-reception-lines" in the stack built so far
      // (added later = painted later = on top), same as the flash/line
      // reorder above and for the same reason it was originally put there
      // ("real node markers... stay on top of a busy fan-out, never
      // occluded by it" — a concern from the old flat-fan model, before
      // Task 31 cut drawn lines to only justified edges). Verified live
      // with map.project() on a real line feature's own two endpoints at
      // the exact moment of a screenshot: the projected pixels landed
      // squarely inside a dense cluster of overlapping node dots (a real
      // neighbour hop's own two endpoints are typically only a few km
      // apart — see buildRouteWalk's own Task 31 comment on median hop
      // length — so they usually sit INSIDE the same dense cluster the
      // line supposedly crosses), and the screenshot showed zero trace of
      // the line — it was solid blue node dots with nothing else, not the
      // faint line the flash/line reorder above was already enough to fix.
      // A thin 1-3px line sitting under an unbroken field of same-or-larger
      // node dots is fully painted over, everywhere the mesh is actually
      // dense enough to have meshview activity in the first place — the
      // exact area this feature exists to show something in. Moving ONLY
      // the line layer (never the node/flash layers, never a colour/width/
      // opacity/timing change) back on top restores its visibility without
      // touching node-dot click/hover targets, which query by layer id, not
      // paint order.
      for (const id of walkLayerOrder("meshview-reception-lines", "meshview-reception-heads")) map.moveLayer(id);
      // Same fix, same reason, for CoreScope's own line layer — see the
      // comment immediately above.
      for (const id of walkLayerOrder("corescore-path-lines", "corescore-path-heads")) map.moveLayer(id);

      for (const layerId of NODE_LAYER_IDS) {
        map.on("click", layerId, (e: MapLayerMouseEvent) => {
          const f = e.features?.[0];
          const id = f?.properties?.id;
          if (id) selectRef.current({ kind: "node", id: String(id) });
        });
        map.on("mouseenter", layerId, (e: MapLayerMouseEvent) => {
          map.getCanvas().style.cursor = "pointer";
          const f = e.features?.[0];
          if (!f) return;
          setHover({
            x: e.point.x,
            y: e.point.y,
            label: String(f.properties?.tip ?? f.properties?.name ?? ""),
          });
        });
        map.on("mousemove", layerId, (e: MapLayerMouseEvent) => {
          setHover((prev) => (prev ? { ...prev, x: e.point.x, y: e.point.y } : prev));
        });
        map.on("mouseleave", layerId, () => {
          map.getCanvas().style.cursor = "";
          setHover(null);
        });
      }

      // Simulated nodes (phase 5): same protocol fill as the real roster
      // (colour still means protocol, per lib/definitions.ts), plus a fixed
      // violet stroke — the existing TRUST_TIER_INFO.simulated colour
      // already defined for provenance, reused rather than inventing a
      // second colour system just for "this one is fake." Hover-only: no
      // click handler, since there's no real node-detail record behind a
      // synthetic id (the Simulated Nodes panel on the Messages page is
      // where these are actually managed).
      if (!map.getSource("sim-nodes")) {
        map.addSource("sim-nodes", { type: "geojson", data: simulatedNodesToGeoJSON(simulatedNodesRef.current) });
        map.addLayer({
          id: "sim-nodes-dots",
          type: "circle",
          source: "sim-nodes",
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["zoom"], 3, 2.6, 8, 3.8, 14, 6.5],
            "circle-color": protocolColorExpr(effectiveTheme),
            "circle-stroke-width": 2,
            "circle-stroke-color": colorFor(TRUST_TIER_INFO.simulated.color, effectiveTheme),
          },
        });
        map.on("mouseenter", "sim-nodes-dots", (e: MapLayerMouseEvent) => {
          map.getCanvas().style.cursor = "default";
          const f = e.features?.[0];
          if (!f) return;
          setHover({ x: e.point.x, y: e.point.y, label: `${String(f.properties?.name ?? "")} (Simulated)` });
        });
        map.on("mousemove", "sim-nodes-dots", (e: MapLayerMouseEvent) => {
          setHover((prev) => (prev ? { ...prev, x: e.point.x, y: e.point.y } : prev));
        });
        map.on("mouseleave", "sim-nodes-dots", () => setHover(null));
      }
    }

    map.on("load", () => {
      addNodeLayers();
      setMapReady(true);
    });
    map.on("style.load", () => {
      addNodeLayers();
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- dark basemap "variant": re-tint the OSM raster layer in place ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getLayer("osm-tiles")) return;
    const p = RASTER_PAINT[resolvedBasemap];
    try {
      map.setPaintProperty("osm-tiles", "raster-brightness-max", p.brightnessMax);
      map.setPaintProperty("osm-tiles", "raster-saturation", p.saturation);
      map.setPaintProperty("osm-tiles", "raster-contrast", p.contrast);
    } catch {
      /* layer not ready yet mid-load; next effect run (mapReady flip) covers it */
    }
  }, [resolvedBasemap, mapReady]);

  // Re-theme AND re-mode the one node-colour expression in place, whenever
  // either the theme or the protocol-filter mode changes (both change which
  // hex values / which property the colour expression reads — see
  // nodeColorExpr's own comment). This used to bake role-colour match
  // expressions in once at layer creation and miss theme flips entirely; now
  // there's exactly one paint property to keep in sync.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getLayer("nodes-dots")) return;
    try {
      map.setPaintProperty("nodes-dots", "circle-color", nodeColorExpr(effectiveTheme, byScope));
      map.setPaintProperty("nodes-dots", "circle-opacity", byScope ? SCOPE_MODE_OPACITY_EXPR : SCOPE_OPACITY_EXPR);
      map.setPaintProperty("nodes-label", "text-color", effectiveTheme === "dark" ? "#ffffff" : "#0b0b0b");
      map.setPaintProperty("nodes-label", "text-halo-color", effectiveTheme === "dark" ? "#1a1a19" : "#fcfcfb");
      if (map.getLayer("nodes-boundary-ring")) {
        map.setPaintProperty("nodes-boundary-ring", "circle-stroke-color", colorFor(REGION_BOUNDARY_COLOR, effectiveTheme));
      }
    } catch {
      /* layer may not exist yet mid-style-swap */
    }
  }, [effectiveTheme, protocolFilter, mapReady, byScope]);

  // meshview-reception-lines no longer has a single theme-dependent paint
  // colour to sync here — since Task 28 it's per-feature (`["get", "color"]`,
  // set above at layer-add time), computed fresh every frame in the route-
  // walk tick effect further down, which already re-runs on effectiveTheme
  // changes (its own dependency array).

  // Simulated-node layer's own two theme-dependent colours (fill = protocol,
  // stroke = the fixed "simulated" violet) — kept in sync the same way as
  // the real layer above, independently since it's a separate layer.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getLayer("sim-nodes-dots")) return;
    try {
      map.setPaintProperty("sim-nodes-dots", "circle-color", protocolColorExpr(effectiveTheme));
      map.setPaintProperty("sim-nodes-dots", "circle-stroke-color", colorFor(TRUST_TIER_INFO.simulated.color, effectiveTheme));
    } catch {
      /* layer may not exist yet mid-style-swap */
    }
  }, [effectiveTheme, mapReady]);

  // --- keep the simulated-node source data fresh ---------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource("sim-nodes") as GeoJSONSource | undefined;
    src?.setData(simulatedNodesToGeoJSON(simulatedNodes) as any);
  }, [simulatedNodes, mapReady]);

  // --- keep node source data fresh (also rebuilds on protocol-filter or
  // node-selection change, since "inScope" is baked into each feature's
  // properties) -------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource("nodes") as GeoJSONSource | undefined;
    const built = nodesToGeoJSON(nodes, protocolFilter, regionScope, selectedNodeId, nearRegionBoundary, tagFilter, scopeView);
    src?.setData(built.collection as any);
    setUnpositionedNodes(built.unpositionedNodes);
    setUnknownProtocolCount(built.unknownProtocol);
  }, [nodes, protocolFilter, selectedRegions, hideOutsideRegions, regionsForNode, nearRegionBoundary, selectedNodeId, tagFilter, scopeView, mapReady]);

  // --- Region reach overlay: a soft shaded area where the chosen scope's packets were heard in the last 7 days
  // (heatmap, weighted by how often each point carried it). A base-map overlay: independent of "Colour by"; markers
  // keep their colouring. The layer sits under the node dots.
  const reachColour = (reachSelected && scopeColours?.scopes.find((x) => reachKey(x.name) === reachSelected)?.colour) || "#3b82f6";
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    try {
      const data = reachGeoJSON(reachScope) as any;
      // The scope's colour, nudged darker on the light map or lighter on the dark one so the shading stands out from
      // the tiles (lib/regionReach.ts's reachHeatColour); stronger and steeper than before, measured on real tiles.
      const shade = reachHeatColour(reachColour, effectiveTheme);
      const heatColour = [
        "interpolate", ["linear"], ["heatmap-density"],
        0, hexToRgba(shade, 0), 0.05, hexToRgba(shade, 0.35), 0.3, hexToRgba(shade, 0.6), 0.7, hexToRgba(shade, 0.8), 1, hexToRgba(shade, 0.92),
      ] as any;
      const src = map.getSource(REACH_HEAT_SOURCE) as GeoJSONSource | undefined;
      if (!src) {
        map.addSource(REACH_HEAT_SOURCE, { type: "geojson", data });
        const before = map.getLayer("nodes-heat") ? "nodes-heat" : undefined;
        map.addLayer(
          {
            id: REACH_HEAT_LAYER, type: "heatmap", source: REACH_HEAT_SOURCE, filter: ["==", ["get", "kind"], "heat"],
            paint: {
              "heatmap-weight": ["get", "w"],
              "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 4, 1.5, 10, 3, 14, 4],
              "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 4, 14, 8, 34, 11, 60, 14, 100],
              "heatmap-opacity": 1,
              "heatmap-color": heatColour,
            },
          },
          before,
        );
      } else {
        src.setData(data);
        map.setPaintProperty(REACH_HEAT_LAYER, "heatmap-color", heatColour);
      }
    } catch {
      /* the style is mid-swap; the next run (theme or data change) adds the layers */
    }
  }, [reachScope, reachColour, mapReady, effectiveTheme]);

  // --- MeshMapper coverage: wardriving coverage as hexes, under the node dots and above the base map. One fixed hex
  // size at every zoom (lib/hexCoverage.ts): hexes never grow when zooming out. The server sends each hex and its coverage
  // type; neighbouring hexes of the same type are drawn as one area (fill, no line inside) with an outline only on the outer
  // edge of the area. The layer only paints: no hover, no tap. The drawing and the redraw after each move are
  // components/map/hexCoverageLayer.ts, shared with the FreqMapper layer. Reads the stored response only
  // (useMeshMapperCoverage): no request to MeshMapper is made from here.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const full = mm.full;
    if (!mmShown || !full) {
      removeHexLayer(map, "mm-coverage");
      mmCellsRef.current = null;
      return;
    }
    const version = full.data_version ?? "";
    if (!mmCellsRef.current || mmCellsRef.current.version !== version) mmCellsRef.current = { version, layer: null };
    const cache = mmCellsRef.current;
    return mountHexLayer(map, {
      id: "mm-coverage",
      // MeshMapper is drawn under FreqMapper, and both under the nodes.
      before: (m) => (m.getLayer("fm-coverage-fill") ? "fm-coverage-fill" : m.getLayer(REACH_HEAT_LAYER) ? REACH_HEAT_LAYER : m.getLayer("nodes-heat") ? "nodes-heat" : undefined),
      fillColour: typeExpression("fill", full.type_colours),
      lineColour: typeExpression("border", full.type_colours),
      fillOpacity: fillOpacity(effectiveTheme),
      load: () => {
        if (!cache.layer) cache.layer = layerCells(cellsFromHexRows(full.hexes, full.fields), full.ref_lat);
        return cache.layer;
      },
    });
    // The paint colours follow the theme; the hexes follow the stored response (its version).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, mmShown, mm.full?.data_version, effectiveTheme]);

  // --- FreqMapper coverage (issue #36): evidence of Meshtastic coverage as hexes, above MeshMapper and under the node dots.
  // The same grid, merging, outline and zoom gate as the MeshMapper layer (the same code); the hexes come from the answer
  // the server built from the stored events (lib/freqmapperCoverage.ts). It only paints. Reads that stored answer only
  // (useFreqMapperCoverage): no request to FreqMapper is made from here.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const full = fm.full;
    if (!fmShown || !full) {
      removeHexLayer(map, "fm-coverage");
      fmCellsRef.current = null;
      return;
    }
    const version = full.data_version ?? "";
    if (!fmCellsRef.current || fmCellsRef.current.version !== version) fmCellsRef.current = { version, layer: null };
    const cache = fmCellsRef.current;
    return mountHexLayer(map, {
      id: "fm-coverage",
      before: (m) => (m.getLayer(REACH_HEAT_LAYER) ? REACH_HEAT_LAYER : m.getLayer("nodes-heat") ? "nodes-heat" : undefined),
      fillColour: fmColourExpression("fill"),
      lineColour: fmColourExpression("outline"),
      fillOpacity: fillOpacity(effectiveTheme),
      load: () => {
        if (!cache.layer) cache.layer = layerCells(cellsFromRows(full.hexes, full.fields), full.ref_lat);
        return cache.layer;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, fmShown, fm.full?.data_version, effectiveTheme]);

  // The map's own box changes width when the live packet drawer folds or opens (and height with the phone sheet), so
  // MapLibre is told to redraw to the new size as the box moves, not only on a window resize.
  useEffect(() => {
    const map = mapRef.current;
    const el = containerRef.current;
    if (!map || !mapReady || !el || typeof ResizeObserver === "undefined") return;
    let frame = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => map.resize());
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [mapReady]);

  // --- initial camera fit: once, when we know where to point the camera ---
  // Priority, per the settings-coverage pass (part 4, 2026-09-27): an
  // operator-configured map.default_center_*/default_zoom (GET
  // /api/config/display) always wins when set — it's an explicit choice,
  // so it must not be raced/overridden by node-position data that happens
  // to arrive first. Failing that, fit to the mesh's own known node
  // positions (unchanged behaviour). Failing THAT (no default configured,
  // no usable position yet), stay at the world view the map was
  // provisionally created with — never a silently-assumed region. This
  // effect deliberately waits on displaySettingsAvailable before doing
  // anything (including the node-position fit) so it never falls through
  // to "fit to nodes" before it's actually known whether an explicit
  // default is configured. Runs on every `nodes`/`displaySettings` update
  // until hasAutoFitRef flips true (an explicit default resolves this
  // immediately; the node-position path is set the first time
  // meshBoundsFromNodes finds at least one usable position), then never
  // runs again for the life of this map instance; also refuses outright
  // once userInteractedRef is set, so a user who pans/zooms during the
  // loading gap is never overridden.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || hasAutoFitRef.current || userInteractedRef.current) return;
    if (!displaySettingsAvailable) return; // don't fall back to node-fit before we know if a default is configured
    const cfg = displaySettings?.map;
    if (cfg && cfg.default_center_lat != null && cfg.default_center_lon != null && cfg.default_zoom != null) {
      hasAutoFitRef.current = true;
      map.jumpTo({ center: [cfg.default_center_lon, cfg.default_center_lat], zoom: cfg.default_zoom });
      return;
    }
    const bounds = meshBoundsFromNodes(nodes);
    if (!bounds) return; // no usable position yet, no configured default — stay at the world view
    hasAutoFitRef.current = true;
    map.fitBounds(bounds, { padding: MESH_FIT_PADDING_PX, maxZoom: MESH_FIT_MAX_ZOOM, duration: 0 });
  }, [nodes, mapReady, displaySettings, displaySettingsAvailable]);

  // Manual re-fit (see MESH_FIT_CONTROL_LABEL's own comment, definitions.ts):
  // the auto-fit above is deliberately one-shot and never overrides a user
  // who has already moved the camera, so this is the only way to get back to
  // a full-mesh view after panning/zooming away from it. Always recomputes
  // bounds fresh from the current node set (not whatever the initial fit
  // saw) and does not touch hasAutoFitRef/userInteractedRef — it neither
  // re-arms nor is gated by the one-shot auto-fit logic above.
  function fitToMesh() {
    const map = mapRef.current;
    if (!map) return;
    const bounds = meshBoundsFromNodes(nodesRef.current);
    if (!bounds) return;
    map.fitBounds(bounds, { padding: MESH_FIT_PADDING_PX, maxZoom: MESH_FIT_MAX_ZOOM });
  }

  // Recompute freshness on a clock tick even when `nodes` hasn't changed,
  // so a ring correctly fades from online -> stale -> offline over time.
  useEffect(() => {
    const id = window.setInterval(() => {
      const map = mapRef.current;
      if (!map || !mapReady) return;
      const src = map.getSource("nodes") as GeoJSONSource | undefined;
      src?.setData(
        nodesToGeoJSON(nodesRef.current, filterRef.current, regionScopeRef.current, selectedNodeIdRef.current, nearRegionBoundaryRef.current, tagFilterRef.current, scopeViewRef.current)
          .collection as any,
      );
    }, 30000);
    return () => window.clearInterval(id);
  }, [mapReady]);

  // --- meshview route-tree walk: recompute each PATH's own travel/trail/
  // fade state and push it all to the one GL source once per animation
  // frame. History, in order: a plain 150ms interval drew every line at
  // full length with one shared opacity for the whole fan ("suddenly
  // appear and then fade all at once"); a per-line pass fixed growth but
  // still fanned every feeder straight from the transmitter regardless of
  // distance, which was conceptually wrong — there should
  // never be a packet that hops clear across the entire map;
  // independently-animated tree edges fixed THAT; a full-root-to-leaf-path
  // pass after that made a longer journey flow continuously instead of
  // hopping in isolation, but reintroduced a different bug caught watching
  // the live map — still machine-gunning from a single location, because
  // EVERY reached node's own full path departed from the root at once (a
  // 39-node tree meant 39 travellers all leaving the same point). Now: one
  // traveller per EDGE (lib/meshview.ts's buildRouteWalk — the branching-
  // wave fix), departing only when the traveller ahead of it arrives at
  // its parent node, so the trunk out of the root is drawn by exactly ONE
  // traveller before splitting into however many children that node has —
  // a wavefront branching outward, not a spray of parallel lines from one
  // dot. Each edge is still walked continuously (lib/animation.ts's
  // routeWalkPathState -> lib/meshview.ts's positionAlongPath/trailSlices),
  // leaving a COMET TRAIL that dissipates from its oldest (tail) end
  // forward as the head advances, fading slowly from the tail's starting
  // point to the final path. MapLibre's
  // line-gradient is a paint property on the whole layer, not per-feature,
  // so a per-traveller gradient can't be expressed with one shared line
  // layer directly — each trail is instead emitted as MESHVIEW_TRAIL_SLICES
  // small LineString features, each with its own `opacity`, approximating
  // the gradient (data-driven `line-opacity` below). A shared trunk edge is
  // now drawn by exactly the one traveller that actually crosses it (not
  // once per downstream leaf as before) — MESHVIEW_TRAIL_HEAD_OPACITY still
  // caps well below 1 purely so genuinely-overlapping trails (e.g. two
  // different packets' walks crossing the same real edge) read brighter
  // without blowing out to a solid slab, not to compensate for the old
  // per-leaf overdraw. `widthScale` (confidence styling —
  // lib/definitions.ts's MESHVIEW_ROUTE_CONFIDENCE_STYLE — multiplied by
  // provenance styling, MESHVIEW_ROUTE_PROVENANCE_STYLE, Task 28) and
  // `color` (meshviewRouteLineColor — traceroute-provenance edges render in
  // the legend's own "Traceroute" magenta, distinct from an inferred
  // edge's external_unauthenticated colour) ride along as feature
  // properties, consumed by the line layer's data-driven expressions below.
  //
  // Once an edge has fully arrived, it renders as one settled/fading shape
  // (no trail effect — it's done moving) — arrival should feel like
  // arrival, then decay on its own clock, no shared expiry across the fan.
  //
  // The ripple animation was found to be a literal (not representative)
  // rendering of a geographic ring, since reverted, and separately the
  // branching-wave lines above were found to still read as machine-gunning
  // from a single point once
  // traceroutes crowded the queue and dominated the frame. Root cause of
  // the spray wasn't the branching shape, it was drawing a full line for
  // EVERY edge regardless of how little is actually known about its
  // parentage — measured: only 21% of edges are "proven"/"evidenced" (a
  // direct reception or a confirmed link), the rest is the backend's own
  // best guess. `walk.paths` at that point held ONLY justified edges (real
  // lines) and `walk.flashes` held everything else. That gate was itself
  // reverted by Task 34 (confidence no longer excludes a line), and Task 35
  // (2026-09-21) then made paths/flashes additive rather than exclusive: a
  // plausible-distance edge now lands in BOTH — a real receiver at a real
  // position, honestly lit once reached, PLUS the line it travelled to get
  // there, since a median <10km hop is only a few px at region zoom and the
  // line alone doesn't read. A flash reuses routeWalkPathState with
  // totalTravelMs=0 (it doesn't travel — it's already arrived the instant
  // its own time comes) so the exact same appear/hold/fade math the lines
  // use governs it too.
  //
  // This needs the same per-frame cadence the ray/pulse overlay already
  // uses, not a coarser tick. AppData.tsx still owns the raw walks
  // (createdAt-stamped at fetch-completion time, capped at an ADAPTIVE
  // concurrency limit — see lib/animation.ts's MESHVIEW_CAP_* and
  // AppData.tsx's own controller, which THIS effect feeds by reporting its
  // own measured frame delta every tick); this is the one place elapsed
  // time turns into actual geometry + opacity. Still a single GeoJSON
  // source with one setData call — NOT a DOM element per path/slice/head,
  // and NOT a second animation system — GL renders this many small
  // primitives at no meaningful per-frame cost on real hardware (see
  // lib/definitions.ts's adaptive-cap comment). Gated on
  // provenanceScope==="firehose" as a second, belt-and-suspenders check —
  // AppData already empties meshviewRouteWalks outside that scope, but
  // checking here too means a stale in-flight walk can never flash back
  // into view for one frame after the toggle flips.
  useEffect(() => {
    let raf = 0;
    let lastFrameAt: number | null = null;
    function tick() {
      const now = performance.now();
      if (lastFrameAt != null) reportMeshviewFrameMs(now - lastFrameAt);
      lastFrameAt = now;

      const map = mapRef.current;
      const src = map?.getSource("meshview-reception-lines") as GeoJSONSource | undefined;
      if (map && mapReady && src) {
        if (provenanceScope !== "firehose") {
          src.setData({ type: "FeatureCollection", features: [] } as any);
        } else {
          // Lines, head and landings come from lib/pathFx.ts, the same look the CoreScope walk uses; the colour is
          // the packet type's. Reduced motion draws each segment whole (no travelling head, trail, orb or ripple).
          const reduced = prefersReducedMotion();
          const features: FxFeature[] = [];
          for (const walk of meshviewRouteWalksRef.current) {
            const elapsed = now - walk.createdAt;
            const typeColour = walk.category ? packetTypeColour(walk.category.protocol, walk.category.type, effectiveTheme) : null;
            let root: { lon: number; lat: number } | null = null;
            for (const path of walk.paths) {
              const state = walkState(path.startOffsetMs, path.totalTravelMs, elapsed);
              if (path.startOffsetMs === 0 && !root) root = path.segments[0].parent;
              if (!state) continue;
              if (state.phase === "travelling" && !reduced) {
                // Comet trail: a short run of fading slices behind the head.
                for (const slice of trailSlices(path.segments, state.elapsedIntoTravel)) {
                  const style = meshviewRouteConfidenceStyle(slice.confidence);
                  const prov = meshviewRouteProvenanceStyle(slice.provenance);
                  features.push(lineFeature(slice.from, slice.to, slice.opacityFraction * LINE_CORE_OPACITY * style.opacityScale * prov.opacityScale, style.widthScale * prov.widthScale, typeColour ?? meshviewRouteLineColor(slice.provenance, effectiveTheme), false));
                }
                const head = positionAlongPath(path.segments, state.elapsedIntoTravel);
                features.push(...headFeatures(head.lon, head.lat, typeColour ?? colorFor(MESHVIEW_FLASH_COLOR, effectiveTheme)));
              } else {
                // Landed (or reduced motion): the whole segment, fading over SEGMENT_FADE_MS after landing.
                for (const seg of path.segments) {
                  const style = meshviewRouteConfidenceStyle(seg.confidence);
                  const prov = meshviewRouteProvenanceStyle(seg.provenance);
                  features.push(lineFeature(seg.parent, seg.child, state.opacity * style.opacityScale * prov.opacityScale, style.widthScale * prov.widthScale, typeColour ?? meshviewRouteLineColor(seg.provenance, effectiveTheme), false));
                }
              }
            }
            // Every reception lands the same way whatever its confidence tier (the tier only styles the line): a
            // small solid burst, an orb and a ripple; and the sender, where the route starts, lands at time zero.
            const landColour = typeColour ?? colorFor(MESHVIEW_FLASH_COLOR, effectiveTheme);
            if (root) features.push(...landingFeatures(root.lon, root.lat, elapsed, landColour, reduced));
            for (const flash of walk.flashes) {
              features.push(...landingFeatures(flash.lon, flash.lat, elapsed - flash.startOffsetMs, landColour, reduced));
            }
          }
          src.setData({ type: "FeatureCollection", features } as any);
        }
      }
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // effectiveTheme: colours are computed fresh inside tick() from this closure, so a theme flip must restart the
    // loop to stop reading the stale light/dark value.
  }, [mapReady, provenanceScope, reportMeshviewFrameMs, effectiveTheme]);

  // --- CoreScope carried-path walk: same per-frame render split as the
  // meshview tick above, applied to a genuinely simpler data shape — one
  // real, already-ordered hop chain per packet, no confidence/provenance
  // tiers to style (lib/corescore.ts's header comment). A SEPARATE
  // requestAnimationFrame loop (not folded into the meshview tick above) so
  // this new, much lighter feature can never risk the already-verified
  // meshview render path — same "reuse the machinery, don't build a second
  // animation system" principle applied to a second, independent GL
  // source rather than a shared one. Gated on BOTH provenanceScope (same
  // "this radio only" contract meshview follows — AppData already empties
  // corescorePathWalks outside "firehose", this is belt-and-suspenders
  // against one stale in-flight frame) AND protocolFilter (spec: a MeshCore
  // path must disappear when "Meshtastic" only is selected, same
  // separation-not-emphasis rule nodesToGeoJSON already applies to node
  // markers).
  useEffect(() => {
    let raf = 0;
    function tick() {
      const now = performance.now();
      const map = mapRef.current;
      const src = map?.getSource("corescore-path-lines") as GeoJSONSource | undefined;
      if (map && mapReady && src) {
        if (provenanceScope !== "firehose" || protocolFilter === "meshtastic") {
          src.setData({ type: "FeatureCollection", features: [] } as any);
        } else {
          const reduced = prefersReducedMotion();
          const features: FxFeature[] = [];
          for (const walk of corescorePathWalksRef.current) {
            // A walk whose spread-out start has not come yet draws nothing (createdAt lies in the future).
            const elapsed = now - walk.createdAt;
            if (elapsed < 0) continue;
            // Colour by packet type; a type we do not know falls back to the MeshCore protocol colour.
            const typeName = meshcorePayloadTypeName(walk.payloadType);
            const colour = typeName ? packetTypeColour("meshcore", typeName, effectiveTheme) : colorFor(PROTOCOL_COLOR.meshcore, effectiveTheme);
            for (const path of walk.paths) {
              const state = walkState(path.startOffsetMs, path.totalTravelMs, elapsed);
              if (!state) continue;
              const widthScale = CORESCORE_PATH_LINE_STYLE.widthScale;
              if (state.phase === "travelling" && !reduced) {
                const segMs = path.totalTravelMs / path.segments.length;
                for (const slice of corescoreTrailSlices(path.segments, state.elapsedIntoTravel, segMs)) {
                  features.push(lineFeature(slice.from, slice.to, slice.opacityFraction * LINE_CORE_OPACITY * CORESCORE_PATH_LINE_STYLE.opacityScale, widthScale, colour, path.bridged));
                }
                const head = corescorePositionAlongPath(path.segments, state.elapsedIntoTravel, segMs);
                features.push(...headFeatures(head.lon, head.lat, colour));
              } else {
                // Landed (or reduced motion): the segment whole, fading over SEGMENT_FADE_MS after landing.
                for (const seg of path.segments) {
                  features.push(lineFeature(seg.parent, seg.child, state.opacity * CORESCORE_PATH_LINE_STYLE.opacityScale, widthScale, colour, path.bridged));
                }
              }
            }
            // Every placed hop, and the reporting station at the end, lands the same way. An unresolved hop does not.
            for (const flash of walk.flashes) {
              features.push(...landingFeatures(flash.lon, flash.lat, elapsed - flash.startOffsetMs, colour, reduced));
            }
          }
          src.setData({ type: "FeatureCollection", features } as any);
        }
      }
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // effectiveTheme: colours are computed fresh inside tick() from this closure, same reason the meshview tick above
    // restarts on a theme flip.
  }, [mapReady, provenanceScope, protocolFilter, effectiveTheme]);

  // --- imperative animation overlay -----------------------------------
  useAnimationOverlay(mapRef, overlayRef, animations, select, protocolFilter, provenanceScope, regionScope, selectedNodeId, setHover);

  return (
    <div className="map-pane">
      {/* Sits ABOVE the map canvas area in document order — on desktop this
          is still visually an absolute overlay in .map-pane's own bottom-
          left corner (unchanged look), but at phone width it drops into
          normal flow as a real block above the map, per
          measurement: at 390x844 this stack used to sit ON TOP of an
          already-tiny map rather than beside it, covering most of what
          little map was left (fine on desktop and
          fatal when the map is small). See .overflow-note-stack's own
          narrow-width rule in global.css. */}
      <LiveMapNotices healthEverLoaded={healthEverLoaded} availableProtocols={availableProtocols} provenanceScope={provenanceScope} setProvenanceScope={setProvenanceScope} overflowCount={overflowCount} unpositionedNodes={unpositionedNodes} unknownProtocolCount={unknownProtocolCount} />

      {/* Everything that positions itself via `inset:0`/hover-relative
          coordinates against ITS OWN nearest positioned ancestor lives in
          this wrapper, not .map-pane directly — .map-pane is now a flex
          column (note-stack, then this, flex:1) so the note-stack above
          can take real space on mobile instead of overlapping. */}
      <div className="map-canvas-area">
        <div ref={containerRef} style={{ position: "absolute", inset: 0, background: MAP_CONTAINER_BACKGROUND[resolvedBasemap] }} />
        <div ref={overlayRef} style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }} />

        {/* Manual re-fit — the one-shot initial auto-fit above never
            overrides a camera the user has already moved, so this is the
            only way back to a full-mesh view after panning/zooming away
            from it. Label from the registry (MESH_FIT_CONTROL_LABEL,
            definitions.ts), not written here. */}
        {!mapUnavailable && <MapCanvasControls fitToMesh={fitToMesh} replay={replay} exitReplay={exitReplay} hover={hover} />}

        {/* The legends: floating panels on the map's left side (CSS 05-map-controls.css), clear of the zoom and
            Fit to mesh controls at the top right and the notes at the bottom left. */}
        {!mapUnavailable && <MapLegends />}
        {mapUnavailable && (
          <div role="alert" style={{ position: "relative", padding: "2rem", maxWidth: "36rem" }}>
            <h2>Map unavailable</h2>
            <p>The map could not start. It requires WebGL2; check that hardware acceleration is enabled in your browser, then reload.</p>
            <p>You can continue using Nodes, Packets, Analysis, and the other pages.</p>
            <button type="button" onClick={() => navigate("nodes")}>Open Nodes</button>
          </div>
        )}
      </div>
    </div>
  );
}
